import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, DatePicker, Input, InputNumber, Select, Tooltip } from 'antd';
import { FormikProps } from 'formik';
import React from 'react';
import { ErrorAwareLabel } from '../../../components/error-aware-label';
import { LabeledField } from '../../../components/form-fields/labeled-field';
import { TextField } from '../../../components/form-fields/text-field';
import { Section } from '../../../components/form-fields/section';
import { CommonScheduleSection } from '../../../components/shared-game-components/game-schedule';
import type { Contest, ContestSkillChallenge } from '../../../stores/contest';
import { ConsentOptions } from './consent-options';
import { FieldError } from './field-error';
import {
    GeneralFormValues,
    Language,
    LANGUAGES,
    MIN_RETENTION_DAYS,
    proposeUrlSegment,
} from './form';
import style from './style.module.scss';

const SKILL_CHALLENGES: { value: ContestSkillChallenge; label: string }[] = [
    { value: 'winner', label: 'The sponsor asks each winner' },
    { value: 'in_tool', label: 'In-app' },
];

const SKILL_CHALLENGE_HELP =
    'The Criminal Code (sections 197 to 206) treats a prize won by pure ' +
    'chance as an illegal lottery. A skill-testing question removes the pure ' +
    'chance: a short arithmetic problem, such as 2 × 6 + 10 − 5, to solve ' +
    'without help. Each person answers it once.';

type CountField =
    | 'minAge'
    | 'maxEntriesPerDay'
    | 'maxEntriesTotal'
    | 'routeMaxEntriesPerDay'
    | 'routeMaxEntriesTotal';

interface GeneralTabProps {
    formik: FormikProps<GeneralFormValues>;
    /** The saved contest, absent while it is being created. */
    contest: Contest | null;
}

export const GeneralTab = ({ formik, contest }: GeneralTabProps) => {
    const { values, setFieldValue } = formik;
    const errorOf = (path: string) =>
        formik.submitCount > 0 ? formik.getFieldMeta(path).error : undefined;
    const hasError = (path: string) => !!errorOf(path);

    /** Empty means no limit, or the age of majority for the minimum age. */
    const countInput = (field: CountField, placeholder: string) => (
        <InputNumber
            min={1}
            precision={0}
            placeholder={placeholder}
            value={values[field]}
            onChange={(value) => setFieldValue(field, value ?? null)}
        />
    );

    // A published contest's address has been shared, so it stays.
    const addressLocked = !!contest?.isActive;

    // The address follows the title until someone types a different one.
    const setTitle = (lang: Language, title: string) => {
        const current = values.translations[lang];
        const followsTitle =
            !addressLocked &&
            (!current.urlSegment ||
                current.urlSegment === proposeUrlSegment(current.title));
        setFieldValue(`translations.${lang}`, {
            title,
            urlSegment: followsTitle
                ? proposeUrlSegment(title)
                : current.urlSegment,
        });
    };

    const noLimitAtAll =
        values.maxEntriesPerDay === null &&
        values.maxEntriesTotal === null &&
        values.routeMaxEntriesPerDay === null &&
        values.routeMaxEntriesTotal === null;

    return (
        <div className={style.general_tab}>
            <Section title="Name and address">
                <div className={style.input_row}>
                    {LANGUAGES.map((lang) => (
                        <div key={lang} className={style.input_col}>
                            <ErrorAwareLabel
                                text={`Title ${lang}`}
                                hasError={hasError(
                                    `translations.${lang}.title`
                                )}
                            />
                            <TextField
                                value={values.translations[lang].title}
                                maxLength={150}
                                onChange={(e) => setTitle(lang, e.target.value)}
                            />
                            <FieldError
                                error={errorOf(`translations.${lang}.title`)}
                            />
                        </div>
                    ))}
                </div>
                <div className={style.input_row}>
                    {LANGUAGES.map((lang) => {
                        const address = contest?.addresses.find(
                            (each) => each.lang === lang
                        );
                        return (
                            <div key={lang} className={style.input_col}>
                                <ErrorAwareLabel
                                    text={`Address ${lang}`}
                                    hasError={hasError(
                                        `translations.${lang}.urlSegment`
                                    )}
                                />
                                <TextField
                                    prefix={`/${lang}/`}
                                    disabled={addressLocked}
                                    value={values.translations[lang].urlSegment}
                                    maxLength={100}
                                    onChange={(e) =>
                                        setFieldValue(
                                            `translations.${lang}.urlSegment`,
                                            e.target.value
                                        )
                                    }
                                />
                                <FieldError
                                    error={errorOf(
                                        `translations.${lang}.urlSegment`
                                    )}
                                />
                                {address && (
                                    <a
                                        className={style.address_link}
                                        href={address.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {address.url}
                                    </a>
                                )}
                            </div>
                        );
                    })}
                </div>
                {!addressLocked && (
                    <p className="footnote_description">
                        The address is the end of the contest link. It cannot
                        change once the contest is published.
                    </p>
                )}
            </Section>

            <Section title="Participation period and provinces">
                <CommonScheduleSection
                    startDate={values.startDate}
                    endDate={values.endDate}
                    timezone={values.timeZone}
                    timezoneField="timeZone"
                    provinces={values.provinces}
                    setFieldValue={setFieldValue}
                    checkIfHasError={hasError}
                />
                <p className="footnote_description">
                    The provinces decide who sees the contest in the
                    applications and who may enter. A contest without a province
                    is shown to nobody.
                </p>
                <LabeledField
                    label="Draw date"
                    helperText="Shown to participants after each entry, as “Draw on <date>”. Leave it empty to show no date."
                >
                    <DatePicker
                        value={values.drawAt}
                        onChange={(date) => setFieldValue('drawAt', date)}
                        size="small"
                    />
                </LabeledField>
            </Section>

            <Section title="Eligibility">
                <LabeledField
                    label="Minimum age"
                    helperText="Leave it empty to require the age of majority of the participant's province."
                    hasError={hasError('minAge')}
                >
                    {countInput('minAge', 'Age of majority')}
                </LabeledField>
                <LabeledField
                    label={
                        <>
                            Skill-testing question
                            <Tooltip title={SKILL_CHALLENGE_HELP}>
                                <InfoCircleOutlined
                                    className={style.info_icon}
                                />
                            </Tooltip>
                        </>
                    }
                >
                    <Select<ContestSkillChallenge>
                        className={style.wide_select}
                        value={values.skillChallenge}
                        onChange={(value) =>
                            setFieldValue('skillChallenge', value)
                        }
                        options={SKILL_CHALLENGES}
                    />
                </LabeledField>
            </Section>

            <Section title="Entry limits per participant">
                <p className="footnote_description">
                    Every way of entering counts: product codes, entries without
                    purchase, referrals, photos. Leave a limit empty for none.
                </p>
                <div className={style.row}>
                    <LabeledField
                        label="Entries per day"
                        hasError={hasError('maxEntriesPerDay')}
                    >
                        {countInput('maxEntriesPerDay', 'No limit')}
                    </LabeledField>
                    <LabeledField
                        label="Entries over the whole contest"
                        hasError={hasError('maxEntriesTotal')}
                    >
                        {countInput('maxEntriesTotal', 'No limit')}
                    </LabeledField>
                </div>
            </Section>

            <Section title="Product codes and entries without purchase">
                <p className="footnote_description">
                    A participant enters with the code of a participating
                    product, or without buying anything. Both ways are worth the
                    same number of chances and share the limits below, since the
                    law requires the free way to offer the same odds.
                </p>
                {noLimitAtAll && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Set at least one limit, here or above. Without any, participants cannot enter with a code or without purchase."
                    />
                )}
                <div className={style.row}>
                    <LabeledField
                        label="Chances per entry"
                        hasError={hasError('routeEntryQuantity')}
                    >
                        <InputNumber
                            min={1}
                            precision={0}
                            value={values.routeEntryQuantity}
                            onChange={(value) =>
                                setFieldValue('routeEntryQuantity', value)
                            }
                        />
                    </LabeledField>
                    <LabeledField
                        label="Entries per day"
                        hasError={hasError('routeMaxEntriesPerDay')}
                    >
                        {countInput('routeMaxEntriesPerDay', 'No limit')}
                    </LabeledField>
                    <LabeledField
                        label="Entries over the whole contest"
                        hasError={hasError('routeMaxEntriesTotal')}
                    >
                        {countInput('routeMaxEntriesTotal', 'No limit')}
                    </LabeledField>
                </div>
                <LabeledField
                    label="No-purchase code"
                    helperText="Printed in the rules. Participants type it where they would type a product code, so it must follow the same format."
                    hasError={hasError('noPurchaseCode')}
                >
                    <Input
                        className={style.single_input}
                        value={values.noPurchaseCode}
                        maxLength={50}
                        onChange={(e) =>
                            setFieldValue('noPurchaseCode', e.target.value)
                        }
                    />
                </LabeledField>
            </Section>

            <Section title="Personal information">
                <LabeledField
                    label="Days kept after the end"
                    helperText={
                        errorOf('retentionDays') ??
                        `Participants' personal information is anonymized once this delay runs out. At least ${MIN_RETENTION_DAYS} days, so it outlives the draw.`
                    }
                    hasError={hasError('retentionDays')}
                >
                    <InputNumber
                        min={MIN_RETENTION_DAYS}
                        precision={0}
                        value={values.retentionDays}
                        onChange={(value) =>
                            setFieldValue('retentionDays', value)
                        }
                    />
                </LabeledField>
            </Section>

            <Section title="Consent checkboxes">
                {hasError('consentOptions') &&
                    typeof formik.errors.consentOptions === 'string' && (
                        <p className={style.error}>
                            {formik.errors.consentOptions}
                        </p>
                    )}
                <ConsentOptions
                    value={values.consentOptions}
                    onChange={(options) =>
                        setFieldValue('consentOptions', options)
                    }
                    hasError={(index) =>
                        formik.submitCount > 0 &&
                        Array.isArray(formik.errors.consentOptions) &&
                        !!formik.errors.consentOptions[index]
                    }
                />
            </Section>
        </div>
    );
};
